import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Heart, Home, LayoutGrid, ShoppingBag, User } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';

const MobileBottomNav = ({ onWishlistOpen }) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { cart } = useCart();
  const location = useLocation();

  if (location.pathname.startsWith('/admin')) return null;

  const cartCount = cart?.total_items || cart?.items?.length || 0;

  const tabs = [
    { path: '/', Icon: Home, label: { en: 'Home', ar: 'الرئيسية' } },
    // "Shop" opens the shop page (search stays one tap away in the header).
    { path: '/products', Icon: LayoutGrid, label: { en: 'Shop', ar: 'تسوق' } },
    { path: '/cart', Icon: ShoppingBag, label: { en: 'Bag', ar: 'الحقيبة' }, badge: cartCount },
    { path: '/products', Icon: Heart, label: { en: 'Saved', ar: 'محفوظ' }, isWishlist: true },
    {
      path: user ? '/account' : `${location.pathname}?auth=login`,
      Icon: User,
      label: user ? { en: 'Account', ar: 'حسابي' } : { en: 'Sign in', ar: 'دخول' },
      isAuth: !user,
    },
  ];

  // Colourful bottom navigation: paper bar, 12 px labels; the active tab is
  // crimson (icon, label, a 3 px top bar and a soft blush pill behind the
  // icon); the bag count is a crimson badge.
  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-50 border-t border-rule-strong bg-paper no-print md:hidden dark:bg-ink"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      aria-label={t({ en: 'Primary', ar: 'التنقل الرئيسي' })}
    >
      <ul className="grid h-[68px] grid-cols-5">
        {tabs.map(({ path, Icon, label, badge, isWishlist, isAuth }) => {
          const isActive = isWishlist || isAuth
            ? false
            : path === '/'
            ? location.pathname === '/'
            : location.pathname.startsWith(path.split('?')[0]);

          const handleClick = isWishlist
            ? (event) => {
                event.preventDefault();
                onWishlistOpen?.();
              }
            : undefined;

          const text = t(label);
          const a11yLabel = badge > 0 ? `${text}, ${badge} ${badge === 1 ? 'item' : 'items'}` : text;

          const inner = (
            <>
              <span
                aria-hidden="true"
                className={`absolute inset-x-0 top-0 mx-auto h-[3px] w-8 rounded-b-full bg-crimson transition-opacity motion-reduce:transition-none ${isActive ? 'opacity-100' : 'opacity-0'}`}
              />
              <span className={`relative flex h-8 w-12 items-center justify-center rounded-full ${isActive ? 'bg-tint-blush' : ''}`} aria-hidden="true">
                <Icon size={21} strokeWidth={isActive ? 2.2 : 1.6} />
                {badge > 0 && (
                  <span className="absolute -top-1 end-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-crimson px-1 text-[14px] font-bold leading-none tabular-nums text-white">
                    {badge > 9 ? '9+' : badge}
                  </span>
                )}
              </span>
              <span className={`text-[12px] leading-none ${isActive ? 'font-semibold' : 'font-medium'}`} aria-hidden="true">
                {text}
              </span>
            </>
          );

          const cls = `relative flex h-full w-full flex-col items-center justify-center gap-1.5 select-none transition-colors ${
            isActive ? 'text-crimson-ink' : 'text-charcoal/75 dark:text-white/80'
          }`;

          return (
            <li key={`${path}-${text}`}>
              {handleClick ? (
                <button type="button" onClick={handleClick} aria-label={a11yLabel} className={cls}>
                  {inner}
                </button>
              ) : (
                <Link to={path} aria-label={a11yLabel} aria-current={isActive ? 'page' : undefined} className={cls}>
                  {inner}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default MobileBottomNav;
